import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircleQuestion, X, Send, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";

const CHIPS = [
  "How do I cash out?",
  "When do ad points credit?",
  "PayPal requirements",
  "Report an offer bug",
];

export default function AiChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hey! I'm Pulse ⚡ your AdsRush assistant. Ask me anything about earning or cashing out." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const sid = useRef(`chat_${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, session_id: sid.current });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: formatApiErrorDetail(e.response?.data?.detail) }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="ai-support-chat-trigger"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full btn-neon flex items-center justify-center shadow-lg"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircleQuestion className="w-6 h-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[520px] rounded-2xl glass flex flex-col overflow-hidden"
            data-testid="ai-chat-drawer"
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-cyan-500/20 bg-[#0C101A]/80">
              <Sparkles className="w-5 h-5 text-cyan-neon" />
              <div>
                <p className="font-display font-bold text-sm leading-none">Pulse Assistant</p>
                <p className="text-xs text-slate-400 mt-0.5">AI support · online</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-cyan-400/15 text-white rounded-br-sm"
                        : "bg-[#121826] text-slate-200 rounded-bl-sm border border-slate-800"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="px-3 py-2 rounded-2xl bg-[#121826] border border-slate-800 text-slate-400 text-sm">
                    Pulse is typing…
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {messages.length <= 2 && (
              <div className="px-3 pb-2 flex flex-wrap gap-2">
                {CHIPS.map((c) => (
                  <button
                    key={c}
                    onClick={() => send(c)}
                    className="text-xs px-2.5 py-1.5 rounded-full border border-cyan-500/30 text-cyan-neon hover:bg-cyan-400/10 transition"
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            <div className="p-3 border-t border-slate-800 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask Pulse…"
                data-testid="ai-chat-input"
                className="flex-1 bg-[#07090E] border border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-cyan-500/50"
              />
              <Button onClick={() => send()} data-testid="ai-chat-send-btn" className="btn-neon h-9 w-9 p-0">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
